import { useState, useEffect } from "react";
import { X, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { useActiveAds } from "@/hooks/useActiveAds";
import AdLink from "@/components/AdLink";
import { getSafeImageUrl } from "@/utils/sanitizeUrl";

export default function GlobalAdBanner() {
  const { lang, isRtl } = useI18n();
  const activeBannerAds = useActiveAds('banner');
  const banner = activeBannerAds.length > 0 ? activeBannerAds[0] : null;
  
  const [closedId, setClosedId] = useState<string | null>(null);
  
  useEffect(() => {
    // Re-show ad if admin edits it and preview event triggers
    const handlePreview = () => setClosedId(null);
    window.addEventListener("kayninAdsUpdated", handlePreview);
    return () => window.removeEventListener("kayninAdsUpdated", handlePreview);
  }, []);

  if (!banner || banner.id === closedId) return null;

  const title = lang === "fr" ? banner.titleFr : lang === "ar" ? banner.titleAr : banner.titleZgh || banner.titleFr;
  const description = lang === "fr" ? banner.descriptionFr : lang === "ar" ? banner.descriptionAr : banner.descriptionZgh || banner.descriptionFr;
  const url = banner.linkUrl;

  const isClosable = banner.isClosable !== false;
  const pos = banner.imagePosition || "left";
  const hasBgText = banner.bgColor !== "bg-background";
  const textColor = hasBgText ? "text-white" : "text-foreground";

  // Determine layout classes based on position
  const getLayoutClasses = () => {
    switch (pos) {
      case "top": return "flex-col";
      case "bottom": return "flex-col-reverse";
      case "right": return "flex-row-reverse items-center";
      case "left": 
      default: return "flex-row items-center";
    }
  };

  // Render Content Block
  const renderContent = () => (
    <div className={`flex flex-col flex-1 ${pos === 'background' ? 'relative z-10 p-6 backdrop-blur-sm bg-black/20 text-white rounded-xl' : 'p-4'}`}>
      {title && <h3 className={`font-display font-bold text-lg leading-tight mb-1 drop-shadow-sm ${pos === 'background' ? 'text-white' : textColor}`}>{title}</h3>}
      {description && <p className={`font-body text-sm opacity-90 line-clamp-3 drop-shadow-sm ${pos === 'background' ? 'text-white/90' : ''}`}>{description}</p>}
      {url && (
        <span className={`mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider ${pos === 'background' ? 'text-white' : textColor} hover:opacity-80 transition-opacity`}>
          {lang === 'fr' ? 'Découvrir' : lang === 'ar' ? 'اكتشف' : 'ⵉⵙⵙⵏ'}
          <ArrowRight className={`w-3.5 h-3.5 ${isRtl ? 'rotate-180' : ''}`} />
        </span>
      )}
    </div>
  );

  // Render Image Block
  const renderImage = () => {
    if (!banner.imageUrl || !getSafeImageUrl(banner.imageUrl)) return null;
    if (pos === "background") return null; // Handled separately

    const imgClasses = pos === "top" || pos === "bottom" 
      ? "w-full h-40 object-contain bg-black/5" 
      : "w-1/3 h-full min-h-[120px] object-contain bg-black/5";

    return (
      <div className={`overflow-hidden shrink-0 ${pos === 'top' || pos === 'bottom' ? 'w-full' : 'w-1/3'}`}>
        <img src={getSafeImageUrl(banner.imageUrl)} alt={title || "Ad"} className={imgClasses} />
      </div>
    );
  };

  const InnerTag = url ? AdLink : "div";

  return (
    <div className={`relative rounded-2xl overflow-hidden shadow-elevated transition-all duration-300 hover:shadow-2xl hover:scale-[1.03] active:scale-[0.98] w-full max-w-sm ml-auto border border-border/10 ${banner.bgColor}`}>
      {isClosable && (
        <button 
          onClick={(e) => { e.preventDefault(); setClosedId(banner.id); }}
          className={`absolute top-2 right-2 z-20 p-1.5 rounded-full backdrop-blur-md transition-colors ${pos === 'background' || hasBgText ? 'bg-black/20 text-white hover:bg-black/40' : 'bg-muted/50 text-foreground hover:bg-muted'}`}
        >
          <X className="w-4 h-4" />
        </button>
      )}

      <InnerTag href={url || "#"} className={`block relative h-full w-full ${url ? 'cursor-pointer' : ''}`}>
        {banner.imageUrl && getSafeImageUrl(banner.imageUrl) && pos === "background" && (
          <div className="absolute inset-0 z-0 bg-black">
            <img src={getSafeImageUrl(banner.imageUrl)} alt="Banner bg" className="w-full h-full object-contain opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/80 mix-blend-multiply"></div>
          </div>
        )}

        <div className={`flex h-full w-full ${pos !== 'background' ? getLayoutClasses() : 'items-end'}`}>
          {renderImage()}
          {renderContent()}
        </div>
      </InnerTag>
    </div>
  );
}
